import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import SubaccountSearchDropdown from '../../molecules/SubaccountSearchDropdown/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import ButtonConfirm from '../../atoms/ButtonConfirm/view';
import Tooltip from '@mui/material/Tooltip';
import { HelpCircle } from 'lucide-react';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import './style.css';

function ReceiptTypeForm({
    initialData = {},
    onSubmit,
    loadingId,
    title,
    submitLabel,
    showDelete = false,
    onDelete,
}) {
    const [receiptType, SetReceiptType] = useState({
        id: '',
        name: '',
        description: '',
        debit_account_id: '',
        credit_account_id: ''
    });

    const [errors, setErrors] = useState({
        name: false
    });

    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            SetReceiptType({
                id: initialData.id || '',
                name: initialData.name || '',
                description: initialData.description || '',
                debit_account_id: initialData.debit_account_id || '',
                credit_account_id: initialData.credit_account_id || ''
            });
        }
    }, [initialData]);

    function handleChange(e) {
        e.preventDefault();
        const { id, value } = e.target;
        const key = id.replace("receipt-type-", "");

        SetReceiptType(prev => ({
            ...prev,
            [key]: value
        }));

        if (key === 'name') {
            setErrors(prev => ({ ...prev, name: value.trim() === '' }));
        }
    }

    function handleSubmit() {
        if (receiptType.name.trim() === '') {
            setErrors(prev => ({ ...prev, name: true }));
            dispatchCustomEvent("popup", { message: "El nombre es obligatorio.", type: "error" });
            return;
        }
        if (!receiptType.debit_account_id || !receiptType.credit_account_id) {
            dispatchCustomEvent("popup", { message: "Ambas cuentas contables son obligatorias.", type: "error" });
            return;
        }
        onSubmit(receiptType);
    }

    return (
        <div className="new-receipt-type-container">
            <h2 className="new-receipt-type-title">{title}</h2>

            <Form action="javascript:void(0);" id="receipt-type-form" style={{ width: '100%', outline: 'none', display: 'block' }}>
                {/* ── Sección 1: Información Básica ── */}
                <div className="new-receipt-type-section-split">
                    <div className="new-receipt-type-sidebar">
                        <h3 className="new-receipt-type-section-title">Información Básica</h3>
                        <p className="new-receipt-type-section-desc">Ingresa el nombre y la descripción general para identificar el tipo de recibo.</p>
                    </div>
                    <div className="new-receipt-type-form-right">
                        <div className="new-receipt-type-fields-row">
                            <div className="new-receipt-type-field-col">
                                <Form.Group className="new-receipt-type-group-custom" controlId="receipt-type-name">
                                    <div className="d-flex align-items-center gap-1 mb-1" style={{ height: '24px' }}>
                                        <Form.Label className="mb-0">Nombre <span className="req">*</span></Form.Label>
                                    </div>
                                    <input
                                        className="new-receipt-type-input-premium"
                                        type="text"
                                        id="receipt-type-name"
                                        placeholder="Ej: Recibo de Caja, Egreso..."
                                        onChange={handleChange}
                                        value={receiptType.name}
                                    />
                                    {errors.name && <div className="text-danger small mt-1">El nombre es obligatorio.</div>}
                                </Form.Group>
                            </div>
                            <div className="new-receipt-type-field-col">
                                <Form.Group className="new-receipt-type-group-custom" controlId="receipt-type-description">
                                    <div className="d-flex align-items-center gap-1 mb-1" style={{ height: '24px' }}>
                                        <Form.Label className="mb-0">Descripción</Form.Label>
                                    </div>
                                    <input
                                        className="new-receipt-type-input-premium"
                                        type="text"
                                        id="receipt-type-description"
                                        placeholder="Ingresa una descripción opcional"
                                        onChange={handleChange}
                                        value={receiptType.description}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                </div>

                <hr className="new-receipt-type-separator" />

                {/* ── Sección 2: Configuración Contable ── */}
                <div className="new-receipt-type-section-split">
                    <div className="new-receipt-type-sidebar">
                        <h3 className="new-receipt-type-section-title">Configuración Contable</h3>
                        <p className="new-receipt-type-section-desc">Asigna las subcuentas auxiliares para los movimientos automáticos de débito y crédito.</p>
                    </div>
                    <div className="new-receipt-type-form-right">
                        <div className="new-receipt-type-fields-row">
                            <div className="new-receipt-type-field-col">
                                <Form.Group className="new-receipt-type-group-custom" id="debit-account-group">
                                    <div className="param-label-container">
                                        <Form.Label className="mb-0">Cuenta Débito <span className="req">*</span></Form.Label>
                                        <Tooltip title="Subcuenta contable donde se registrarán los cargos del débito." arrow placement="top">
                                            <span className="param-help-icon"><HelpCircle size={14} /></span>
                                        </Tooltip>
                                    </div>
                                    <SubaccountSearchDropdown
                                        onSelected={(e) => SetReceiptType(prev => ({ ...prev, debit_account_id: e.id }))}
                                        selected={receiptType.debit_account_id ? { id: receiptType.debit_account_id } : null}
                                        data={{ users_accounts_id: receiptType.debit_account_id }}
                                    />
                                </Form.Group>
                            </div>
                            <div className="new-receipt-type-field-col">
                                <Form.Group className="new-receipt-type-group-custom" id="credit-account-group">
                                    <div className="param-label-container">
                                        <Form.Label className="mb-0">Cuenta Crédito <span className="req">*</span></Form.Label>
                                        <Tooltip title="Subcuenta contable donde se registrarán los abonos del crédito." arrow placement="top">
                                            <span className="param-help-icon"><HelpCircle size={14} /></span>
                                        </Tooltip>
                                    </div>
                                    <SubaccountSearchDropdown
                                        onSelected={(e) => SetReceiptType(prev => ({ ...prev, credit_account_id: e.id }))}
                                        selected={receiptType.credit_account_id ? { id: receiptType.credit_account_id } : null}
                                        data={{ users_accounts_id: receiptType.credit_account_id }}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                </div>

            </Form>
            <div className="new-receipt-type-footer-actions">
                {showDelete && onDelete && (
                    <ButtonConfirm
                        eventKey="delete-receipt-type"
                        onAcept={onDelete}
                        msg="¿Estás seguro de eliminar este tipo de recibo?"
                        title="Eliminar Tipo de Recibo"
                        className="btn-secondary-danger"
                    />
                )}
                <ButtonPrimary
                    onClick={handleSubmit}
                    id={loadingId}
                    title={submitLabel}
                    showLoading={true}
                />
            </div>
        </div>
    );
}

export default ReceiptTypeForm;
